import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddToHomeScreenRoundedIcon from "@mui/icons-material/AddToHomeScreenRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type PromptPriority = "normal" | "high";

interface InstallAppCoachProps {
  appId: string;
  appName: string;
  enabled?: boolean;
  priority?: PromptPriority;
}

const NORMAL_DISMISS_HOURS = 48;
const HIGH_PRIORITY_DISMISS_HOURS = 18;

function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    nav.standalone === true
  );
}

function detectIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  const iDevice = /iphone|ipad|ipod/.test(ua);
  const ipadDesktopMode =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iDevice || ipadDesktopMode;
}

function readDismissUntil(key: string): number {
  try {
    const value = localStorage.getItem(key);
    const asNumber = value ? Number(value) : 0;
    return Number.isFinite(asNumber) ? asNumber : 0;
  } catch {
    return 0;
  }
}

function writeDismissUntil(key: string, hours: number): void {
  try {
    const until = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem(key, String(until));
  } catch {
    // Ignore storage errors silently.
  }
}

export default function InstallAppCoach({
  appId,
  appName,
  enabled = true,
  priority = "normal",
}: InstallAppCoachProps) {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [open, setOpen] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [installed, setInstalled] = useState<boolean>(() => detectStandalone());

  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const isTouchPointer = useMediaQuery("(pointer:coarse)");
  const isMobileLike = isSmallScreen || isTouchPointer;
  const isIos = useMemo(() => detectIos(), []);

  const dismissKey = `wedding_a2hs_${appId}_dismiss_until`;
  const supportsNativePrompt = Boolean(deferredPrompt);
  const canShowPrompt = supportsNativePrompt || isIos;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setDeferredPrompt(installEvent);
    };

    const onInstalled = () => {
      setInstalled(true);
      setOpen(false);
      setDeferredPrompt(null);
    };

    window.addEventListener(
      "beforeinstallprompt",
      onBeforeInstallPrompt as EventListener,
    );
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        onBeforeInstallPrompt as EventListener,
      );
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!enabled || installed) return;

    const onEngaged = () => {
      if (Date.now() < readDismissUntil(dismissKey)) return;
      setOpen(true);
    };

    window.addEventListener("wedding:a2hs-engaged", onEngaged as EventListener);
    return () => {
      window.removeEventListener(
        "wedding:a2hs-engaged",
        onEngaged as EventListener,
      );
    };
  }, [dismissKey, enabled, installed]);

  useEffect(() => {
    if (!enabled || installed || !isMobileLike || !canShowPrompt) {
      return;
    }

    if (Date.now() < readDismissUntil(dismissKey)) {
      return;
    }

    const delay = priority === "high" ? 600 : 2200;
    const timer = window.setTimeout(() => setOpen(true), delay);
    return () => window.clearTimeout(timer);
  }, [canShowPrompt, dismissKey, enabled, installed, isMobileLike, priority]);

  const dismiss = () => {
    const hours = priority === "high" ? HIGH_PRIORITY_DISMISS_HOURS : NORMAL_DISMISS_HOURS;
    writeDismissUntil(dismissKey, hours);
    setOpen(false);
    setShowIosSteps(false);
  };

  const handlePrimaryAction = async () => {
    if (!deferredPrompt) {
      setShowIosSteps((prev) => !prev);
      return;
    }

    try {
      await deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setOpen(false);
      } else {
        dismiss();
      }
    } catch {
      dismiss();
    } finally {
      setDeferredPrompt(null);
    }
  };

  if (!enabled || installed || !isMobileLike || !canShowPrompt || !open) {
    return null;
  }

  const title = supportsNativePrompt
    ? `Save ${appName} to your home screen`
    : `Add ${appName} to Home Screen`;

  const subtitle = supportsNativePrompt
    ? "Open faster and keep an app-like full-screen experience."
    : "Use Safari Share to add a shortcut and launch it like an app.";

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: { xs: "calc(84px + env(safe-area-inset-bottom, 0px))", sm: 16 },
        px: 2,
        zIndex: 1400,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: "100%",
          maxWidth: 560,
          borderRadius: 3,
          p: 2,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(253,248,244,0.98) 100%)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={1.25}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "weddingInstallPulse 1.8s ease-in-out infinite",
                "@keyframes weddingInstallPulse": {
                  "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(139,111,94,0.35)" },
                  "70%": {
                    transform: "scale(1.08)",
                    boxShadow: "0 0 0 10px rgba(139,111,94,0)",
                  },
                  "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(139,111,94,0)" },
                },
              }}
            >
              <AddToHomeScreenRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={handlePrimaryAction}
              startIcon={supportsNativePrompt ? <PhoneIphoneRoundedIcon /> : <IosShareRoundedIcon />}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
            >
              {supportsNativePrompt ? "Add to Home Screen" : showIosSteps ? "Hide iPhone steps" : "Show iPhone steps"}
            </Button>
            <Button
              variant="text"
              onClick={dismiss}
              sx={{ borderRadius: 999, textTransform: "none" }}
            >
              Not now
            </Button>
          </Stack>

          <Collapse in={!supportsNativePrompt && showIosSteps}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                iPhone quick steps:
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  1. Tap the <IosShareRoundedIcon sx={{ fontSize: 15, verticalAlign: "text-bottom" }} /> Share button in Safari.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  2. Select "Add to Home Screen".
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3. Tap "Add" to save {appName} to your phone.
                </Typography>
              </Stack>
            </Paper>
          </Collapse>
        </Stack>
      </Paper>
    </Box>
  );
}
